import { useState, useCallback } from 'react';
import { api } from '@/lib/api';

interface InviteLinkButtonProps {
  vehicleId: string;
}

export function InviteLinkButton({ vehicleId }: InviteLinkButtonProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [linkData, setLinkData] = useState<{ url: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = useCallback(async () => {
    setState('loading');
    try {
      const res = await api.post('/workshops/my/invite-links', { vehicleId });
      setLinkData({ url: res.data.url, expiresAt: res.data.expiresAt });
      setState('success');
    } catch {
      setState('idle');
    }
  }, [vehicleId]);

  const handleCopy = useCallback(async () => {
    if (!linkData) return;
    await navigator.clipboard.writeText(linkData.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [linkData]);

  const handleWhatsApp = useCallback(() => {
    if (!linkData) return;
    const text = encodeURIComponent(
      `Acesse o histórico do seu veículo no MustAuto: ${linkData.url}`,
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  }, [linkData]);

  if (state === 'idle') {
    return (
      <button
        onClick={handleGenerate}
        className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted/80 transition-colors"
        title="Gerar link de acesso ao histórico"
      >
        🔗 Invite
      </button>
    );
  }

  if (state === 'loading') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-muted-foreground">
        Gerando...
      </span>
    );
  }

  // state === 'success'
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs dark:border-emerald-900/60 dark:bg-emerald-950/20">
      <input
        readOnly
        value={linkData?.url ?? ''}
        className="w-full rounded border border-emerald-300 bg-card px-2 py-1 text-xs font-mono text-foreground select-all dark:border-emerald-900/60"
        onFocus={(e) => e.target.select()}
      />
      <div className="flex items-center gap-1.5">
        <button
          onClick={handleCopy}
          className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {copied ? 'Copiado!' : 'Copiar'}
        </button>
        <button
          onClick={handleWhatsApp}
          className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors"
        >
          WhatsApp
        </button>
        <span className="text-muted-foreground ml-auto">7 dias, uso único</span>
      </div>
    </div>
  );
}
